import React, { useState, useEffect } from 'react';
import { Gift, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { getCustomerId } from '@/utils/customerIdentifier';

interface CartLoyaltySectionProps {
  restaurantId: string;
  totalAmount: number;
  onPointsRedemption: (discount: number, pointsUsed: number) => void;
  currency?: string;
  className?: string;
}

interface CustomerLoyalty {
  current_points: number;
  total_earned_points: number;
  total_redeemed_points: number;
}

interface LoyaltyProgram {
  id: string;
  name: string;
  points_per_dollar: number;
  min_points_to_redeem: number;
  point_value: number;
  is_active: boolean;
}

export const CartLoyaltySection: React.FC<CartLoyaltySectionProps> = ({ 
  restaurantId, 
  totalAmount,
  onPointsRedemption,
  currency = 'USD',
  className = "" 
}) => {
  const [loyaltyData, setLoyaltyData] = useState<CustomerLoyalty | null>(null);
  const [loyaltyProgram, setLoyaltyProgram] = useState<LoyaltyProgram | null>(null);
  const [usePoints, setUsePoints] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoyaltyData();
  }, [restaurantId]);

  useEffect(() => {
    // Calculate redemption when toggle changes
    if (loyaltyData && loyaltyProgram) {
      if (usePoints) {
        const maxRedeemablePoints = Math.min(
          loyaltyData.current_points,
          Math.floor(totalAmount / loyaltyProgram.point_value)
        );
        
        // Round down to nearest redemption increment
        const pointsToUse = Math.floor(maxRedeemablePoints / loyaltyProgram.min_points_to_redeem) * loyaltyProgram.min_points_to_redeem;
        const discount = pointsToUse * loyaltyProgram.point_value;
        
        onPointsRedemption(discount, pointsToUse);
      } else {
        onPointsRedemption(0, 0);
      }
    }
  }, [usePoints, loyaltyData, loyaltyProgram, totalAmount, onPointsRedemption]);

  const fetchLoyaltyData = async () => {
    try {
      const customerId = getCustomerId();

      // Fetch loyalty program details
      const { data: programData } = await supabase
        .from('loyalty_programs')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .single();

      if (programData) {
        setLoyaltyProgram(programData);

        // Fetch customer loyalty points
        const { data: loyaltyData } = await supabase
          .from('customer_loyalty_points')
          .select('*')
          .eq('customer_id', customerId)
          .eq('restaurant_id', restaurantId)
          .single();

        if (loyaltyData) {
          setLoyaltyData(loyaltyData);
        } else {
          // Create initial loyalty record for new customer
          const { data: newLoyalty } = await supabase
            .from('customer_loyalty_points')
            .insert({
              customer_id: customerId,
              restaurant_id: restaurantId,
              current_points: 0,
              total_earned_points: 0,
              total_redeemed_points: 0
            })
            .select()
            .single();

          if (newLoyalty) {
            setLoyaltyData(newLoyalty);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !loyaltyProgram || !loyaltyData) {
    return null;
  }

  const pointsToEarn = Math.floor(totalAmount * loyaltyProgram.points_per_dollar);
  const maxRedeemablePoints = Math.min(
    loyaltyData.current_points,
    Math.floor(totalAmount / loyaltyProgram.point_value)
  );
  const actualPointsToUse = Math.floor(maxRedeemablePoints / loyaltyProgram.min_points_to_redeem) * loyaltyProgram.min_points_to_redeem;
  const maxDiscount = actualPointsToUse * loyaltyProgram.point_value;
  const canRedeem = loyaltyData.current_points >= loyaltyProgram.min_points_to_redeem && maxDiscount > 0;

  return (
    <Card className={`p-4 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 ${className}`}>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-purple-700">
          <Gift className="h-4 w-4" />
          <span className="font-medium">{loyaltyProgram.name || 'Loyalty Program'}</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="text-center">
            <div className="font-semibold text-purple-600">{loyaltyData.current_points}</div>
            <div className="text-gray-600">Current Points</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-green-600 flex items-center justify-center gap-1">
              <TrendingUp className="h-3 w-3" />
              +{pointsToEarn}
            </div>
            <div className="text-gray-600">Points to Earn</div>
          </div>
        </div>

        {canRedeem && (
          <div className="border-t border-purple-200 pt-3">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="use-points" className="text-sm font-medium">
                  Use {actualPointsToUse} points
                </Label>
                <p className="text-xs text-gray-600">
                  Save {currency} {maxDiscount.toFixed(2)}
                </p>
              </div>
              <Switch
                id="use-points"
                checked={usePoints}
                onCheckedChange={setUsePoints}
              />
            </div>
          </div>
        )}

        {!canRedeem && loyaltyData.current_points > 0 && (
          <div className="text-xs text-gray-500 text-center">
            Need {loyaltyProgram.min_points_to_redeem - loyaltyData.current_points} more points to redeem
          </div>
        )}

        {loyaltyData.current_points === 0 && (
          <div className="text-xs text-gray-500 text-center">
            Start earning points with your first order!
          </div>
        )}
      </div>
    </Card>
  );
}; 